import {
  type MaintenanceChecklistDefinition,
  type MaintenanceChecklistField,
  type MaintenanceChecklistFieldType,
  type MaintenanceChecklistSection,
  maintenanceChecklistDefinitionSchema,
  maintenanceChecklistFieldSchema,
} from '@gb-ticket/shared-types';

function newId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
}

function defaultField(
  type: MaintenanceChecklistFieldType,
  sortOrder: number,
): MaintenanceChecklistField {
  const base = {
    fieldKey: newId('field'),
    label: 'Neues Feld',
    helpText: null,
    required: false,
    sortOrder,
  };
  switch (type) {
    case 'info':
      return { ...base, type, content: 'Hinweistext', required: false };
    case 'boolean':
      return { ...base, type };
    case 'choice':
      return {
        ...base,
        type,
        options: [{ value: 'a', label: 'Option A' }],
        allowMultiple: false,
      };
    case 'short_text':
      return { ...base, type, maxLength: 200 };
    case 'long_text':
      return { ...base, type, maxLength: 2000 };
    case 'integer':
      return { ...base, type };
    case 'decimal':
      return { ...base, type, decimalPlaces: 2 };
    case 'measurement':
      return { ...base, type, unit: 'bar' };
    case 'date':
      return { ...base, type };
    case 'time':
      return { ...base, type };
    case 'attachment':
      return { ...base, type, acceptImagesOnly: true };
    default:
      return { ...base, type: 'boolean' };
  }
}

export function parseChecklistDefinition(raw: unknown): MaintenanceChecklistDefinition {
  const fallback = maintenanceChecklistDefinitionSchema.parse({ schemaVersion: 1, sections: [] });
  const parsed = maintenanceChecklistDefinitionSchema.safeParse(raw);
  return parsed.success ? parsed.data : fallback;
}

export function addSection(
  definition: MaintenanceChecklistDefinition,
  title: string,
): MaintenanceChecklistDefinition {
  const sortOrder = definition.sections.length;
  const section: MaintenanceChecklistSection = {
    id: newId('section'),
    title: title.trim() || 'Neuer Abschnitt',
    sortOrder,
    fields: [],
  };
  return maintenanceChecklistDefinitionSchema.parse({
    ...definition,
    sections: [...definition.sections, section],
  });
}

export function removeSection(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
): MaintenanceChecklistDefinition {
  const sections = definition.sections
    .filter((s) => s.id !== sectionId)
    .map((s, index) => ({ ...s, sortOrder: index }));
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

export function addField(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  type: MaintenanceChecklistFieldType,
): MaintenanceChecklistDefinition {
  const sections = definition.sections.map((section) => {
    if (section.id !== sectionId) {
      return section;
    }
    const sortOrder = section.fields.length;
    return {
      ...section,
      fields: [...section.fields, defaultField(type, sortOrder)],
    };
  });
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

export function duplicateField(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  fieldKey: string,
): MaintenanceChecklistDefinition {
  const sections = definition.sections.map((section) => {
    if (section.id !== sectionId) {
      return section;
    }
    const source = section.fields.find((f) => f.fieldKey === fieldKey);
    if (!source) {
      return section;
    }
    const copy = {
      ...source,
      fieldKey: newId('field'),
      sortOrder: section.fields.length,
    };
    return { ...section, fields: [...section.fields, copy] };
  });
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

export function removeField(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  fieldKey: string,
): MaintenanceChecklistDefinition {
  const sections = definition.sections.map((section) => {
    if (section.id !== sectionId) {
      return section;
    }
    const fields = section.fields
      .filter((f) => f.fieldKey !== fieldKey)
      .map((f, index) => ({ ...f, sortOrder: index }));
    return { ...section, fields };
  });
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

function reorder<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length) {
    return items;
  }
  const next = items.slice();
  const [moved] = next.splice(fromIndex, 1);
  if (moved === undefined) {
    return items;
  }
  next.splice(toIndex, 0, moved);
  return next;
}

export function moveSection(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  direction: 'up' | 'down',
): MaintenanceChecklistDefinition {
  const index = definition.sections.findIndex((s) => s.id === sectionId);
  if (index < 0) {
    return definition;
  }
  const target = direction === 'up' ? index - 1 : index + 1;
  const sections = reorder(definition.sections, index, target).map((s, sortOrder) => ({
    ...s,
    sortOrder,
  }));
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

export function moveField(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  fieldKey: string,
  direction: 'up' | 'down',
): MaintenanceChecklistDefinition {
  const sections = definition.sections.map((section) => {
    if (section.id !== sectionId) {
      return section;
    }
    const index = section.fields.findIndex((f) => f.fieldKey === fieldKey);
    if (index < 0) {
      return section;
    }
    const target = direction === 'up' ? index - 1 : index + 1;
    const fields = reorder(section.fields, index, target).map((f, sortOrder) => ({
      ...f,
      sortOrder,
    }));
    return { ...section, fields };
  });
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

export type ChecklistFieldPatch = {
  label?: string;
  helpText?: string | null;
  required?: boolean;
  placement?: MaintenanceChecklistField['placement'];
  content?: string;
  unit?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  options?: { value: string; label: string }[];
  allowMultiple?: boolean;
  decimalPlaces?: number;
};

export function updateField(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  fieldKey: string,
  patch: ChecklistFieldPatch,
): MaintenanceChecklistDefinition {
  const sections = definition.sections.map((section) => {
    if (section.id !== sectionId) {
      return section;
    }
    const fields = section.fields.map((field) =>
      field.fieldKey === fieldKey ? maintenanceChecklistFieldMerge(field, patch) : field,
    );
    return { ...section, fields };
  });
  return maintenanceChecklistDefinitionSchema.parse({ ...definition, sections });
}

function maintenanceChecklistFieldMerge(
  field: MaintenanceChecklistField,
  patch: ChecklistFieldPatch,
): MaintenanceChecklistField {
  const merged = { ...field, ...patch } as MaintenanceChecklistField;
  return maintenanceChecklistFieldSchema.parse(merged);
}
